import { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { MonthStat }      from "../chartUtils";
import { PedidosModalContent, ModalEntityTitle } from "./PedidosModalContent";
import { lineDots } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}
function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[200px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">
            {item.name === "Qtd."
              ? formatNumber(item.value)
              : formatCurrency(item.value)}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 mt-2">Clique na linha para ver detalhes</p>
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props { data: MonthStat[]; loading: boolean; }

export function EvolucaoPrecos({ data, loading }: Props) {
  const [selected, setSelected] = useState<MonthStat | null>(null);

  const avgTicket =
    data.length > 0 ? data.reduce((s, d) => s + d.ticketMedio, 0) / data.length : 0;

  /* LineChart onClick: nextState tem activePayload com os dados do ponto */
  const handleChartClick = (nextState: unknown) => {
    const state = nextState as { activePayload?: { payload: MonthStat }[] };
    const entry = state?.activePayload?.[0]?.payload;
    if (entry) setSelected(entry);
  };

  return (
    <>
      <ChartContainer
        title="Evolução de Valores por Mês"
        subtitle="Ticket médio e valor total ao longo do período"
        loading={loading}
        empty={!loading && data.length === 0}
        onExpand={() => setSelected(data[data.length - 1] ?? null)}
        height={280}
      >
        <LineChart
          data={data}
          margin={{ top: 8, right: 56, left: 0, bottom: 4 }}
          onClick={handleChartClick}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="ticket"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={60}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <YAxis
            yAxisId="valor"
            orientation="right"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={60}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />

          {avgTicket > 0 && (
            <ReferenceLine
              yAxisId="ticket"
              y={avgTicket}
              stroke="#cbd5e1"
              strokeDasharray="4 4"
              label={{ value: "Média", fontSize: 10, fill: "#94a3b8", position: "insideTopLeft" }}
            />
          )}

          <Line
            yAxisId="ticket"
            type="monotone"
            dataKey="ticketMedio"
            name="Ticket Médio"
            stroke="#B03858"
            strokeWidth={2.5}
            {...lineDots("#B03858", { dotR: 4, activeR: 8 })}
          />
          <Line
            yAxisId="valor"
            type="monotone"
            dataKey="valor"
            name="Valor Total"
            stroke="#3b82f6"
            strokeWidth={2}
            strokeDasharray="5 3"
            {...lineDots("#3b82f6")}
          />
        </LineChart>
      </ChartContainer>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected && (
          <ModalEntityTitle
            icon={<TrendingUp className="w-4 h-4" />}
            label={selected.mes}
            iconBg="bg-purple-50"
            iconColor="text-purple-600"
          />
        )}
      >
        {selected && (
          <PedidosModalContent pedidos={selected.pedidos} expandable />
        )}
      </Modal>
    </>
  );
}
